import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import DailyActiveUsersContext from '../../../../contexts/DailyActiveUsersContext';
import { getMonthTwoDigits, toUTCDateString } from '../../../../utils/date';
import { DailyActiveUserPaper } from '../styles';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { origin, REPORT_PERIOD_SELECT } from '../../../../shared/SegmentEvents';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_2 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';

const COLUMN_KEYS = ['dateReference', 'total'];

export interface DailyActiveUserUsageProps {
  isDetailedView?: boolean;
}

const DailyActiveUserUsageComponent: FC<DailyActiveUserUsageProps> = ({ isDetailedView = false }) => {
  const {
    dateToSearch,
    content,
    tenant,
    loggedUser,
    trackingProps,
    setDailyActiveUserUsage,
    dailyActiveUserUsage,
    billingDailyActiveUserApi,
    periodOptions,
    setDateToSearch,
    getPlanStatus,
  } = useContext(DailyActiveUsersContext);
  const [isLoading, setLoading] = useState(false);

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data: dailyActiveUserUsage });

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const storageType =
          (await FeatureToggleService.isDatabricksStorageTypeEnabled(BillingMetrics.dailyActiveUser)) ? 1 : 0;
        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const data = await billingDailyActiveUserApi.getDailyActiveUserUsage(
          tenant.info.id,
          monthString,
          -1,
          storageType,
        );
        setDailyActiveUserUsage(
          data.map((obj) => ({ ...obj, dateReference: toUTCDateString(obj.dateReference, content.locale) })),
        );
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [dateToSearch]);

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.dateReference}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.dateReference}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.total.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale],
  );

  const periodOptionChangeEvent = async (selectedOption) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.DAUPERDATE,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.dailyUsage.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <DailyActiveUserPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.dailyUsage.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>
          {content.dailyUsage.tableInformation}
        </BdsTypo>
        <TableWithPagination
          options={{
            heading: [content.dailyUsage.byDaily.date, content.dailyUsage.byDaily.dailyActiveUsers],
            columnKeys: COLUMN_KEYS,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </DailyActiveUserPaper>
    </>
  );
};

export default DailyActiveUserUsageComponent;
